import { computed, inject, Injectable, signal } from '@angular/core';

import { catchError, of, tap } from 'rxjs';

import { ProductUpdatesHttpService } from './product-updates-http.service';
import {
  PRODUCT_UPDATES_CONFIG,
  type ProductUpdatesConfig,
} from '../config/product-updates-config.provider';
import type {
  ProductCategory,
  ProductUpdate,
  ProductUpdatesQueryParams,
  UpdateCategory,
} from '../models/product-update.model';

export type MainTab = 'whats-new' | 'coming-soon' | 'changelog' | 'timeline';
export type ProductTab = 'all' | 'hire' | 'manage' | 'grow' | 'ai';

// Cache structure for per-tab data storage
interface CachedData {
  data: ProductUpdate[];
  totalCount: number;
  currentPage: number;
}

@Injectable({ providedIn: 'root' })
export class ProductUpdatesStateService {
  private readonly httpService = inject(ProductUpdatesHttpService);
  private readonly config = inject<ProductUpdatesConfig>(PRODUCT_UPDATES_CONFIG);

  // Pagination state (private)
  private readonly currentPage = signal(1);
  private readonly totalCount = signal(0);
  private readonly perPage = 6; // Number of posts per page

  // Per-tab cache: Map<cacheKey, CachedData>
  // This allows each tab combination to maintain its own cached data
  private readonly dataCache = new Map<string, CachedData>();

  // Request tracking to handle race conditions
  private currentRequestId = 0;

  private getCacheKey(mainTab: MainTab, productTab: ProductTab): string {
    return `${mainTab}:${productTab}`;
  }

  // Public members must come after private injects due to TypeScript initialization order
  /* eslint-disable @typescript-eslint/member-ordering */

  // State signals
  public readonly productUpdates = signal<ProductUpdate[]>([]);
  public readonly loading = signal(true); // Start true for initial skeleton display on first app load
  public readonly loadingMore = signal(false); // Loading more posts via infinite scroll
  public readonly error = signal<string | null>(null);

  // Current article for article detail view
  public readonly currentArticle = signal<ProductUpdate | null>(null);

  // Track if we're on an article page (set immediately on navigation, before data loads)
  public readonly isArticlePage = signal(false);

  // Tab signals - initialize based on current URL to prevent header flash
  public readonly activeMainTab = signal<MainTab>(this.detectInitialMainTab());
  public readonly activeProductTab = signal<ProductTab>('all');

  // Computed: whether more posts are available to load
  public readonly hasMore = computed(() => {
    const loaded = this.productUpdates().length;
    const total = this.totalCount();
    return loaded < total;
  });

  // Filter signals
  public readonly selectedProductCategories = signal<ProductCategory[]>([]);
  public readonly selectedUpdateCategories = signal<UpdateCategory[]>([]);
  public readonly currentLocale = signal(this.detectInitialLocale());

  // Computed state - filtering is now done server-side, so just return productUpdates
  public readonly filteredUpdates = computed(() => this.productUpdates());

  public readonly hasActiveFilters = computed(
    () => this.selectedProductCategories().length > 0 || this.selectedUpdateCategories().length > 0,
  );

  public readonly availableLocales = computed(() => this.config.availableLocales);

  public loadProductUpdates(forceReload = false): void {
    const currentMainTab = this.activeMainTab();
    const currentProductTab = this.activeProductTab();
    const cacheKey = this.getCacheKey(currentMainTab, currentProductTab);

    // Increment request ID immediately to invalidate any in-flight requests
    // This ensures stale responses don't overwrite data when switching tabs
    const requestId = ++this.currentRequestId;

    // Check if we have cached data for this tab combination
    const cached = this.dataCache.get(cacheKey);

    // Use cache if available (unless force reload)
    if (!forceReload && cached !== undefined) {
      this.productUpdates.set(cached.data);
      this.totalCount.set(cached.totalCount);
      this.currentPage.set(cached.currentPage);
      this.loading.set(false);
      return;
    }

    // No cache - need to fetch data. Show loading state for user feedback.
    this.loading.set(true);
    this.error.set(null);
    this.currentPage.set(1);

    this.httpService
      .fetchProductUpdatesPaginated(this.buildQueryParams(1))
      .pipe(
        tap(response => {
          const data = response.data;
          const totalCount = response.meta?.total ?? data.length;

          // Always cache the result (it's valid data for that tab combination)
          this.dataCache.set(cacheKey, {
            data,
            totalCount,
            currentPage: 1,
          });

          // Only update state if this is still the active request
          if (requestId === this.currentRequestId) {
            this.productUpdates.set(data);
            this.totalCount.set(totalCount);
            this.currentPage.set(1);
            this.loading.set(false);
          }
        }),
        catchError((err: unknown) => {
          // Only update error state if this is still the active request
          if (requestId === this.currentRequestId) {
            this.error.set('Failed to load product updates. Please try again.');
            this.loading.set(false);
          }
          console.error('Error loading product updates:', err);
          return of({ data: [], meta: { total: 0, page: 1, perPage: this.perPage } });
        }),
      )
      .subscribe();
  }

  /**
   * Build query params based on current state (tabs and locale)
   */
  private buildQueryParams(page: number): ProductUpdatesQueryParams {
    const params: ProductUpdatesQueryParams = {
      locale: this.currentLocale(),
      page,
      perPage: this.perPage,
    };

    // Add update categories filter based on main tab
    const mainTab = this.activeMainTab();
    if (mainTab === 'coming-soon') {
      params.updateCategories = ['Coming soon'];
    } else if (mainTab === 'changelog') {
      params.updateCategories = ['Changelog'];
    } else if (mainTab === 'whats-new') {
      params.updateCategories = ['Feature', 'Improvement', 'New'];
    }
    // Note: timeline doesn't filter by update category

    // Add product categories filter based on product tab
    const productTab = this.activeProductTab();
    if (productTab !== 'all') {
      const productMap: Record<string, ProductCategory> = {
        hire: 'Hire',
        manage: 'Manage',
        grow: 'Grow',
        ai: 'AI',
      };
      params.productCategories = [productMap[productTab]];
    }

    return params;
  }

  /**
   * Load ALL product updates without pagination (for timeline view)
   * Still applies product tab filter but not main tab filter
   */
  public loadAllProductUpdates(): void {
    const currentProductTab = this.activeProductTab();
    const cacheKey = this.getCacheKey('timeline', currentProductTab);

    // Increment request ID immediately to invalidate any in-flight requests
    // This ensures stale responses don't overwrite data when switching tabs
    const requestId = ++this.currentRequestId;

    // Check if we have cached data for this timeline + product combination
    const cached = this.dataCache.get(cacheKey);

    // Use cache if available
    if (cached !== undefined) {
      this.productUpdates.set(cached.data);
      this.totalCount.set(cached.totalCount);
      this.loading.set(false);
      return;
    }

    // No cache - need to fetch data. Show loading state for user feedback.
    this.loading.set(true);
    this.error.set(null);

    // Build params for timeline (no mainTab filter, just product filter)
    const params: ProductUpdatesQueryParams = {
      locale: this.currentLocale(),
      page: 1,
      perPage: 1000, // Load all posts at once
    };

    // Add product categories filter based on product tab
    if (currentProductTab !== 'all') {
      const productMap: Record<string, ProductCategory> = {
        hire: 'Hire',
        manage: 'Manage',
        grow: 'Grow',
        ai: 'AI',
      };
      params.productCategories = [productMap[currentProductTab]];
    }

    this.httpService
      .fetchProductUpdatesPaginated(params)
      .pipe(
        tap(response => {
          const data = response.data;
          const totalCount = response.meta?.total ?? data.length;

          // Always cache the result (it's valid data for that tab combination)
          this.dataCache.set(cacheKey, {
            data,
            totalCount,
            currentPage: 1,
          });

          // Only update state if this is still the active request
          if (requestId === this.currentRequestId) {
            this.productUpdates.set(data);
            this.totalCount.set(totalCount);
            this.loading.set(false);
          }
        }),
        catchError((err: unknown) => {
          // Only update error state if this is still the active request
          if (requestId === this.currentRequestId) {
            this.error.set('Failed to load product updates. Please try again.');
            this.loading.set(false);
          }
          console.error('Error loading all product updates:', err);
          return of({ data: [], meta: { total: 0, page: 1, perPage: 1000 } });
        }),
      )
      .subscribe();
  }

  public loadMoreProductUpdates(): void {
    // Don't load if already loading or no more data
    if (this.loadingMore() || this.loading() || !this.hasMore()) {
      return;
    }

    const nextPage = this.currentPage() + 1;
    const currentMainTab = this.activeMainTab();
    const currentProductTab = this.activeProductTab();
    const cacheKey = this.getCacheKey(currentMainTab, currentProductTab);

    this.loadingMore.set(true);

    // Track this request
    const requestId = ++this.currentRequestId;

    this.httpService
      .fetchProductUpdatesPaginated(this.buildQueryParams(nextPage))
      .pipe(
        tap(response => {
          // Only update state if this is still the active request
          if (requestId === this.currentRequestId) {
            // Append new updates to existing list
            const newData = [...this.productUpdates(), ...response.data];
            const totalCount = response.meta?.total ?? this.totalCount();

            this.productUpdates.set(newData);
            this.currentPage.set(nextPage);
            this.totalCount.set(totalCount);

            // Update cache with accumulated data
            this.dataCache.set(cacheKey, {
              data: newData,
              totalCount,
              currentPage: nextPage,
            });
          }

          this.loadingMore.set(false);
        }),
        catchError((err: unknown) => {
          console.error('Error loading more product updates:', err);
          this.loadingMore.set(false);
          return of({
            data: [],
            meta: { total: this.totalCount(), page: nextPage, perPage: this.perPage },
          });
        }),
      )
      .subscribe();
  }

  public setLocale(locale: string): void {
    if (this.config.availableLocales.includes(locale)) {
      this.currentLocale.set(locale);
      this.loadProductUpdates();
    }
  }

  public setMainTab(tab: MainTab): void {
    this.activeMainTab.set(tab);
    // Don't trigger load here - wait for setProductTab which is called after
    // This ensures both mainTab and productTab are set before loading
  }

  public setProductTab(tab: ProductTab): void {
    this.activeProductTab.set(tab);
    // Load data - cache logic will decide if fetch needed
    if (this.activeMainTab() === 'timeline') {
      this.loadAllProductUpdates();
    } else {
      this.loadProductUpdates();
    }
  }

  public toggleProductCategory(category: ProductCategory): void {
    this.selectedProductCategories.update((current: ProductCategory[]) => {
      if (current.includes(category)) {
        return current.filter((c: ProductCategory) => c !== category);
      }
      return [...current, category];
    });
  }

  public toggleUpdateCategory(category: UpdateCategory): void {
    this.selectedUpdateCategories.update((current: UpdateCategory[]) => {
      if (current.includes(category)) {
        return current.filter((c: UpdateCategory) => c !== category);
      }
      return [...current, category];
    });
  }

  public setProductCategory(category: ProductCategory | null): void {
    if (category === null) {
      this.selectedProductCategories.set([]);
    } else {
      this.selectedProductCategories.set([category]);
    }
  }

  public clearFilters(): void {
    this.selectedProductCategories.set([]);
    this.selectedUpdateCategories.set([]);
  }

  public setCurrentArticle(article: ProductUpdate | null): void {
    this.currentArticle.set(article);
  }

  public setIsArticlePage(isArticle: boolean): void {
    this.isArticlePage.set(isArticle);
  }

  public getArticleBySlug(slug: string): ProductUpdate | undefined {
    return this.productUpdates().find(article => article.slug === slug);
  }

  /* eslint-enable @typescript-eslint/member-ordering */

  private detectInitialMainTab(): MainTab {
    const path = window.location.pathname;
    if (path === '/' || path === '' || path.startsWith('/timeline')) {
      return 'timeline';
    } else if (path.startsWith('/coming-soon')) {
      return 'coming-soon';
    } else if (path.startsWith('/changelog')) {
      return 'changelog';
    } else if (path.startsWith('/whats-new')) {
      return 'whats-new';
    }
    return 'timeline'; // Default to timeline since it's now the homepage
  }

  private detectInitialLocale(): string {
    const browserLang = navigator.language?.split('-')[0] || this.config.defaultLocale;
    return this.config.availableLocales.includes(browserLang)
      ? browserLang
      : this.config.defaultLocale;
  }
}
